import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { updateRecentQueryCache } from '../search/util';
import {
  orgTracersSchema,
  savedTracerSchema,
  tokenFlowResults,
  transactionFlowResults,
} from './zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import socketClient from 'stateManagement/socket/socketClient';

import { queryClient } from 'stateManagement/queries/client';

import { ORG_SERVICES } from 'blockscope/static/blockscopeServices';
import { TRACER_TOAST_ID } from 'blockscope/static/toastIds';

import { extractAddress } from 'blockscope/utils/helperUtils';
import { shortenedText } from 'blockscope/utils/stringUtils';
import {
  axiosErrorToast,
  axiosSuccessToast,
} from 'blockscope/utils/toastHandler';

import { CURRENT_INVESTIGATION_STORAGE_KEY } from 'blockscope/components/investigations/InvestigationStore';

export const tracerTokenFlowQueryKey = ({
  filterAddress,
  chain,
  startTime,
  endTime,
  tokenAddress,
  tokenType,
}) => [
  'tracer',
  'token-flow',
  {
    chain,
    filterAddress: extractAddress(filterAddress).toLowerCase(),
    tokenType,
    tokenAddress: extractAddress(tokenAddress).toLowerCase(),
    startTime,
    endTime,
  },
];

export const tracerTransactionQueryKey = ({ chain, transactionHash }) => [
  'tracer',
  'transaction',
  chain,
  transactionHash,
];
const tracerTokenFlowEndpoint = async (inputData) => {
  const requestBody = {
    ...inputData,
    filterAddresses: [extractAddress(inputData.filterAddress)],
    tokens: [
      {
        tokenType: inputData.tokenType,
        tokenAddress: extractAddress(inputData.tokenAddress),
      },
    ],
  };
  const res = await axios.post(`/api/v2/tracer/token-flow`, requestBody, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (res?.data?.dataPayload?.tokenFlow?.length > 0) {
    return res.data.dataPayload.tokenFlow[0];
  }
  return { params: requestBody, results: [] };
};

export const useGetTracerTokenFlow_Mutation = () => {
  return useMutation(
    (inputData) =>
      axios
        .post(`/api/v2/tracer/token-flow`, inputData, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => {
          return res.data.dataPayload.tokenFlow || [];
        }),
    {
      onSuccess: (data) => {
        let noResults = true;
        data.forEach((query) => {
          const parseResult = tokenFlowResults.safeParse(query);

          if (parseResult.success === false) {
            throw new Error(
              `Failed to parse tracer response ${parseResult.error}`
            );
          }

          const { params, results } = parseResult.data;

          queryClient.setQueryData(
            tracerTokenFlowQueryKey(params),
            () => parseResult.data
          );

          if (results.length > 0) {
            noResults = false;
          }
        });

        if (noResults) {
          toast.warning('No results found!');
        }
      },
      onError: (err) => {
        axiosErrorToast(err);
      },
    }
  );
};
export const useTracerTokenflow_Query = (tokenFlowData) => {
  if (
    !tokenFlowData ||
    !tokenFlowData.filterAddress ||
    tokenFlowData.filterAddress === '' ||
    !tokenFlowData.chain ||
    !tokenFlowData.startTime ||
    !tokenFlowData.endTime ||
    !tokenFlowData.tokenType
  ) {
    return useQuery({
      queryKey: ['tracer', 'token-flow'],
      queryFn: () => {
        return null;
      },
    });
  }

  return useQuery({
    queryKey: tracerTokenFlowQueryKey(tokenFlowData),
    queryFn: async () => {
      const data = await tracerTokenFlowEndpoint(tokenFlowData);

      const parseResult = tokenFlowResults.safeParse(data);

      if (parseResult.success === false) {
        throw new Error(`Failed to parse tracer response ${parseResult.error}`);
      }

      return parseResult.data;
    },
    cacheTime: Infinity,
    staleTime: Infinity,
    retry: false,
    onError: (err) => axiosErrorToast(err, false, TRACER_TOAST_ID.ERROR),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};

const tracerTransactionEndpoint = async (chain, transactionHash) => {
  const res = await axios.post(
    `/api/v2/tracer/transaction`,
    { chain, transactions: [transactionHash] },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
  if (res?.data?.data) {
    return res.data.data[0];
  }
  return { params: { chain, transactionHash }, results: [] };
};

export const useGetTracerTransaction_Mutation = () => {
  return useMutation(
    /**
     * @param {Object} inputData
     * @param {string} inputData.chain
     * @param {string[]} inputData.transactions
     * @returns {Promise<Object>}
     */
    (inputData) =>
      axios
        .post(`/api/v2/tracer/transaction`, inputData, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => {
          return res.data.data;
        }),
    {
      onSuccess: (data) => {
        data.forEach((query) => {
          const parseResult = transactionFlowResults.safeParse(query);
          if (parseResult.success === false) {
            throw new Error(
              `Failed to parse tracer response ${parseResult.error}`
            );
          }
          const {
            params: { chain, transactionHash },
            results: { tokenFlow, failed, txnTime },
          } = parseResult.data;
          queryClient.setQueryData(
            tracerTransactionQueryKey({ chain, transactionHash }),
            {
              ...parseResult.data,
              txnTime: txnTime * 1000,
            }
          );

          if (failed) {
            toast.warning(
              `${shortenedText(
                transactionHash
              )} Failed Transaction. No tokens moved.`
            );
          } else if (tokenFlow.length === 0) {
            toast.warning(
              `${shortenedText(
                transactionHash
              )} No token transfers found in txn`
            );
          }
        });
      },
      onError: (err) => {
        axiosErrorToast(err, false, TRACER_TOAST_ID.ERROR);
      },
    }
  );
};
export const useTracerTransaction_Query = (chain, transactionHash) => {
  if (!chain || !transactionHash) {
    return useQuery({
      queryKey: ['tracer', 'transaction'],
      queryFn: () => {
        return null;
      },
    });
  }
  return useQuery({
    queryKey: tracerTransactionQueryKey({ chain, transactionHash }),
    queryFn: async () => {
      const data = await tracerTransactionEndpoint(chain, transactionHash);

      const parseResult = transactionFlowResults.safeParse(data);

      if (parseResult.success === false) {
        throw new Error(`Failed to parse tracer response ${parseResult.error}`);
      }

      return parseResult.data;
    },
    cacheTime: Infinity,
    staleTime: Infinity,
    retry: false,
    onError: (err) => axiosErrorToast(err, false, TRACER_TOAST_ID.ERROR),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};

const analysisKey = (chain, tracerId) => `${tracerId}:${chain}`;
const analysisPromises = {};
socketClient.socket.on('tracer:analysis:response', (data) => {
  const {
    request: { chain, tracerId },
    dataAnalysis,
  } = data;
  const key = analysisKey(chain, tracerId);
  if (analysisPromises[key]) {
    analysisPromises[key].resolve(dataAnalysis);
    delete analysisPromises[key];
  }
});

socketClient.socket.on('tracer:analysis:error', (err) => {
  const {
    request: { chain, tracerId },
    message,
  } = err;

  const key = analysisKey(chain, tracerId);
  if (analysisPromises[key]) {
    analysisPromises[key].reject(message);
    delete analysisPromises[key];
  }
});
const tracerDataAnalysisEndpoint = async (inputData) => {
  socketClient.socket.emit('tracer:analysis:request', inputData);
  const newPromise = new Promise((resolve, reject) => {
    analysisPromises[analysisKey(inputData.chain, inputData.tracerId)] = {
      resolve,
      reject,
    };
  });
  await updateRecentQueryCache(
    ORG_SERVICES.TRACER_ANALYTICS,

    {
      chain: inputData.chain,
      addresses: inputData.addresses,
      startTime: inputData.startTime,
      endTime: inputData.endTime,
      tracerId: inputData.tracerId,
    },
    inputData.user,
    false
  );
  return newPromise;
};

export const useTracerDataAnalysis_Query = (
  chain,
  addresses,
  startTime,
  endTime,
  tracerId,
  user
) => {
  if (
    !chain ||
    !addresses ||
    addresses?.length === 0 ||
    !startTime ||
    !endTime
  ) {
    return useQuery({
      queryKey: ['tracer', 'data-analysis'],
      queryFn: () => {
        return null;
      },
    });
  }

  return useQuery({
    queryKey: ['tracer', 'data-analysis', chain, addresses, startTime, endTime],
    queryFn: () =>
      tracerDataAnalysisEndpoint({
        chain,
        addresses,
        startTime,
        endTime,
        tracerId,
        user,
      }),
    cacheTime: Infinity,
    staleTime: Infinity,
    retry: false,

    onError: (err) => {
      axiosErrorToast(err, false, TRACER_TOAST_ID.ERROR);
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};

const getTracerEndpoint = async (tracerId) => {
  const res = await axios.get(`api/v2/tracer/${tracerId}`);
  return res.data.dataPayload.tracer;
};
export const useGetTracer_Query = (tracerId) => {
  if (!tracerId || tracerId === 'unsaved') {
    return useQuery({
      queryKey: ['tracer'],
      queryFn: () => {
        return null;
      },
    });
  }

  return useQuery({
    queryKey: ['tracer', tracerId],
    queryFn: async () => {
      if (!tracerId) {
        throw new Error('missing tracerId');
      }

      const data = await getTracerEndpoint(tracerId);

      const parseResult = savedTracerSchema.safeParse(data);

      if (parseResult.success === false) {
        throw new Error(`Failed to parse tracer response ${parseResult.error}`);
      }

      return parseResult.data;
    },
    cacheTime: 30 * 60 * 1000, // 20 minutes,
    staleTime: 15 * 60 * 1000, // 10 minutes
    retry: false,
    onError: (err) => axiosErrorToast(err, false, TRACER_TOAST_ID.ERROR),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};

const getOrgTracerEndpoint = async () => {
  const res = await axios.get(`api/v2/tracer/`);
  return res.data.dataPayload.tracers;
};
export const useGetOrgTracer_Query = () => {
  return useQuery({
    queryKey: ['org', 'tracers'],
    queryFn: async () => {
      const data = await getOrgTracerEndpoint();
      const parseResult = orgTracersSchema.safeParse(data);

      if (parseResult.success === false) {
        throw new Error(`Failed to parse tracer response ${parseResult.error}`);
      }

      return parseResult.data;
    },
    cacheTime: 30 * 60 * 1000, // 20 minutes,
    staleTime: 15 * 60 * 1000, // 10 minutes
    retry: false,
    onError: (err) => axiosErrorToast(err, false, TRACER_TOAST_ID.ERROR),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};

export const useCreateTracer_Mutation = () => {
  const [, setSearchParams] = useSearchParams();

  const investigationId = localStorage.getItem(
    CURRENT_INVESTIGATION_STORAGE_KEY
  );

  return useMutation(
    /**
     * @param {{tracerObject: import('./zod').SavedTracer}} inputData
     */
    (inputData) =>
      axios
        .post(
          `/api/v2/tracer/`,
          {
            ...inputData.tracerObject,
            investigationId: investigationId ?? undefined,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )
        .then((res) => {
          setSearchParams({ tracerId: res.data.dataPayload.tracer._id });
          return res.data.dataPayload;
        }),
    {
      onSuccess: async (data) => {
        axiosSuccessToast(data.res);
        await queryClient.cancelQueries(['org', 'tracers']);
        queryClient.setQueryData(['org', 'tracers'], (oldTracerList) =>
          oldTracerList ? [data.tracer, ...oldTracerList] : oldTracerList
        );
      },
      onError: (err) => {
        axiosErrorToast(err);
      },
    }
  );
};

export const useUpdateTracer_Mutation = () => {
  return useMutation(
    /**
     *
     * @param {Object} inputData
     * @param {import('./zod').SavedTracer &{tracerId:string}} inputData.tracerObject
     * @param {boolean} inputData.autoSave
     * @returns
     */
    (inputData) =>
      axios
        .put(
          `/api/v2/tracer/${inputData.tracerObject.tracerId}`,
          { ...inputData.tracerObject },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )
        .then((res) => res.data.dataPayload),
    {
      onSuccess: async (data, inputData) => {
        if (!inputData.autoSave) {
          axiosSuccessToast('Saved Tracer');
        }

        await queryClient.cancelQueries([
          'tracer',
          inputData.tracerObject.tracerId,
        ]);

        queryClient.setQueryData(
          ['tracer', inputData.tracerObject.tracerId],
          () => {
            return data.updatedTracer;
          }
        );

        await queryClient.cancelQueries(['org', 'tracers']);

        queryClient.setQueryData(['org', 'tracers'], (oldTracerList) =>
          oldTracerList
            ? oldTracerList.map((tracer) => {
                if (tracer._id === inputData.tracerObject.tracerId) {
                  return data.updatedTracer;
                }
                return tracer;
              })
            : oldTracerList
        );
      },
      onError: (err) => {
        axiosErrorToast(err);
      },
    }
  );
};

export const useDeleteTracer_Mutation = () => {
  return useMutation(
    /**
     * @param {{tracerId: string, navigate: function}} inputData
     */
    (inputData) => {
      axios
        .delete(`/api/v2/tracer/${inputData.tracerId}`, {})
        .then((res) => res.data.message);
    },
    {
      onSuccess: async (data, inputData) => {
        axiosSuccessToast(data);
        await queryClient.cancelQueries(['org', 'tracers']);

        queryClient.setQueryData(['org', 'tracers'], (oldTracerList) =>
          oldTracerList
            ? oldTracerList.filter(
                (tracer) => tracer._id !== inputData.tracerId
              )
            : oldTracerList
        );
        inputData.navigate(`/my-tracers`);
      },
      onError: (err) => {
        axiosErrorToast(err);
      },
    }
  );
};
